import { Logger } from "../../util/logger";

const logger = new Logger("delegateFocus");

/**
* Makes a container element behave like an interactive wrapper for a given form control.
* Clicks (or touch) on the container will delegate focus and interaction to the control.
*
* @template {HTMLElement} T
* @param {T} control - The form control element to receive focus (e.g., input, select, textarea).
* @param {string|HTMLElement} containerOrSelector - Either a container element, or a selector string for control.closest().
* @param {Partial<import("./public").DelegateFocusOptions>} [options] - Optional settings.
* @returns {() => void} A cleanup function to remove event listeners & attached CSS classes.
*/
export function delegateFocus(control, containerOrSelector, options = {}) {
  const {
    enableHoverStyle = false,
    enableContainerStyle = true,
    containerClass = "interactive-container",
    containerHoverClass = "interactive-hover",
  } = options;
  
  const container = typeof containerOrSelector === "string"
  ? control.closest(containerOrSelector)
  : containerOrSelector;
  
  if (!container) {
    logger.error("Container not found for control.", control);
    return () => {};
  }
  
  if (enableContainerStyle) {
    container.classList.add(containerClass);
  }
  
  /** @type {(e: Event) => void} */
  const handlePointerEvent = (e) => {
    if (document.activeElement !== control) {
      control.focus();
      e.preventDefault();
    }
  };

  /** @type {(hover: boolean, e: Event) => void} */
  const onHover = (hover, e) => {
    container.classList.toggle(containerHoverClass, hover);
  }
  const onHoverStart = onHover.bind(null, true);
  const onHoverEnd = onHover.bind(null, false);
  
  container.addEventListener("mousedown", handlePointerEvent);
  container.addEventListener("touchstart", handlePointerEvent, { passive: false });
  
  if (enableHoverStyle) {
    container.addEventListener("mouseenter", onHoverStart);
    container.addEventListener("mouseleave", onHoverEnd);
  }

  return () => {
    container.removeEventListener("mousedown", handlePointerEvent);
    container.removeEventListener("touchstart", handlePointerEvent);

    if (enableContainerStyle) {
      container.classList.remove(containerClass);
    }

    if (enableHoverStyle) {
      container.addEventListener("mouseenter", onHoverStart);
      container.addEventListener("mouseleave", onHoverEnd);
      container.classList.remove(containerHoverClass);
    }
  }
}
